import { createFileRoute } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { FAQ_PAGE_HANDLE, fetchPageByHandle, type ShopifyPage } from "@/lib/shopify";
import { StaticFaqSections, staticFaqPairs } from "@/components/static-faq";

const faqPageQueryOptions = queryOptions({
  queryKey: ["shopify-page", FAQ_PAGE_HANDLE],
  // Swallow Shopify errors so the static FAQ always renders.
  queryFn: () => fetchPageByHandle(FAQ_PAGE_HANDLE).catch(() => null),
  staleTime: 5 * 60 * 1000,
});

/**
 * Parse a Shopify Page bodyHtml into a flat list of Q&A pairs for FAQPage
 * JSON-LD. Assumes H3 = question, following P/UL/text until the next H2/H3 =
 * answer. Section H2 headings are ignored for the schema (Google's FAQPage
 * is flat).
 */
function extractFaqPairs(html: string): { q: string; a: string }[] {
  const stripTags = (s: string) =>
    s
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\s+/g, " ")
      .trim();

  const pairs: { q: string; a: string }[] = [];
  const blockRe = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h[23][^>]*>|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = blockRe.exec(html)) !== null) {
    const q = stripTags(m[1]);
    const a = stripTags(m[2]);
    if (q && a) pairs.push({ q, a });
  }
  return pairs;
}

export const Route = createFileRoute("/faq")({
  loader: ({ context }) => context.queryClient.ensureQueryData(faqPageQueryOptions),
  head: ({ loaderData }) => {
    const page = loaderData as ShopifyPage | null | undefined;
    const title = page?.title
      ? `${page.title} · House of Apocrypha`
      : "FAQ, Shipping & Returns · House of Apocrypha";
    const description =
      "Answers about craft, ingredients, shipping, returns and how to live with a House of Apocrypha natural perfume.";
    const shopifyPairs = page?.bodyHtml ? extractFaqPairs(page.bodyHtml) : [];
    const pairs = [...shopifyPairs, ...staticFaqPairs()];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://houseofapocrypha.com/faq" },
      ],
      links: [{ rel: "canonical", href: "https://houseofapocrypha.com/faq" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "FAQ", item: "https://houseofapocrypha.com/faq" },
            ],
          }),
        },
        ...(pairs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: pairs.map((p) => ({
                    "@type": "Question",
                    name: p.q,
                    acceptedAnswer: { "@type": "Answer", text: p.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: FaqPage,
});

function FaqPage() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24 pt-16">
        <p className="eyebrow text-gold">FAQ</p>
        <h1 className="mt-4 font-display text-5xl tracking-[0.04em] text-gradient-gold sm:text-6xl">
          Frequently Asked Questions
        </h1>
        <p className="mt-6 max-w-2xl font-serif text-lg italic text-muted-foreground">
          Frequently asked questions about craft, ingredients, ordering,
          shipping, returns, and how to live with a natural perfume.
        </p>

        <StaticFaqSections />
      </main>

      <SiteFooter />
    </div>
  );
}
